// @flow
import React, {useEffect} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import Cookie from 'js-cookie';
import Experiment from '@homeaway/react-experiment';
import GoogleOneTapLogin from '@homeaway/ha-google-one-tap';
import SiteHeader from '@homeaway/react-site-header';
import {media} from '../../utils/breakpoints';
import {TripBoardCoachMark} from '@homeaway/react-trip-board-button';
import {useModalRoute} from '@homeaway/react-router-modal';
import LoginExperience from '../LoginExperience/LoginExperience';
import withLingua from '../withLingua/withLingua';
import userActions from '../../actions/user';
import {MODALS} from '../../utils/constants';
import {
    TransitionBanner,
    HOSTAPP
} from '@homeaway/react-brand-transition-banner';

import type {LinguaProps} from '../withLingua/withLingua';
import type {InternalToolsType} from '../InternalTools/InternalTools';
import {
    HA_IDA_SERP_GOOGLEONETAP,
    HA_IDA_SERP_HEADER_LOGIN_TEST,
    HA_IDA_V3_LOGIN_TEST,
    HA_GX_PRE_BRAND_TRANSITION,
    HA_GX_POST_BRAND_TRANSITION
} from '../../abacus/experiments';
import ConnectedSkipToContentAnchor from '../ConnectedSkipToContent/ConnectedSkipToContentAnchor';

import type {Node} from 'react';
import type {State as ReduxState} from '../../reducers/index';

type StateProps = {
    siteName: string,
    theme: string,
    isMobile: boolean,
    isTouchDevice: boolean,
    fullLocale: string,
    currency: string,
    brand: string,
    headerData: {},
    isUserLogged: boolean,
    marketingOptInType: string,
    preferencePageType: string
};

type DispatchProps = {
    setLoggedIn: typeof userActions.setLoggedIn
};

type OwnProps = {
    internalTools?: InternalToolsType,
    showBrandTransitionBanner: string,
    feedbackComponentLingua: {}
};

type Props = OwnProps & StateProps & LinguaProps & DispatchProps;

type ScratchpadCallback = {
    scratchpadLink: Node
};

export const HeaderBar = ({
    siteName,
    theme,
    isMobile,
    isTouchDevice,
    internalTools,
    isUserLogged,
    fullLocale,
    currency,
    showBrandTransitionBanner,
    headerData,
    feedbackComponentLingua,
    setLoggedIn,
    marketingOptInType,
    preferencePageType
}: Props) => {
    const [
        displayNotificationJewel,
        setDisplayNotificationJewel
    ] = React.useState(false);
    const [displayGoogleFrame, setDisplayGoogleFrame] = React.useState(false);
    const [loginModalOpen, setLoginModalOpen] = useModalRoute(MODALS.LOGIN);

    useEffect(() => {
        setLoggedIn({isLoggedIn: isUserLogged});
    }, [isUserLogged, setLoggedIn]);

    const handleShowModal = () => setLoginModalOpen(true);
    const handleCloseModal = () => setLoginModalOpen(false);
    const handleLoginSuccess = (options?: {publicUuid: string}) => {
        if (options && options.publicUuid) {
            window.analyticsdatalayer.publicuuid = options.publicUuid;
        }
        setLoggedIn({isLoggedIn: true});
    };
    const handleTrayChange = (opened: boolean) => {
        if (opened) {
            setDisplayGoogleFrame(true);
        } else {
            setDisplayGoogleFrame(false);
            setDisplayNotificationJewel(false);
        }
    };

    const handleGetTripBoard = ({scratchpadLink}: ScratchpadCallback) => {
        if (!isMobile) {
            return <TripBoardCoachMark scratchpadLink={scratchpadLink} />;
        }
        return null;
    };

    const loginProps = {
        modalMode: true,
        actionLocation: 'search',
        onClose: handleCloseModal,
        marketingOptInType,
        preferencePageType
    };

    const siteHeaderProps = {
        dataUrl: '/serp/api/header',
        siteName,
        theme,
        isMobile: isTouchDevice,
        data: headerData,
        displayTrayJewel: displayNotificationJewel,
        fetchUserData: isUserLogged,
        fullLocale,
        onTrayChange: handleTrayChange,
        internalTools,
        currencyCode: currency,
        tripboard: handleGetTripBoard,
        withoutCountryLanguageSelector: true,
        withoutCurrencySelector: false,
        withoutLocaleSwitcher: false,
        feedbackComponentLingua,
        children: <ConnectedSkipToContentAnchor id="skipToSiteWideNavigation" />
    };

    const vgdcCookie = Cookie.get('vgdc');
    const vgdc = showBrandTransitionBanner || vgdcCookie;

    return (
        <div className="HeaderBar" role="group">
            <Experiment name={HA_IDA_SERP_HEADER_LOGIN_TEST}>
                {({On, Off}) => (
                    <React.Fragment>
                        <On>
                            <SiteHeader
                                {...siteHeaderProps}
                                travelerLoginClickHandler={handleShowModal}
                            />
                        </On>
                        <Off>
                            <SiteHeader {...siteHeaderProps} />
                        </Off>
                    </React.Fragment>
                )}
            </Experiment>

            <Experiment name={HA_GX_PRE_BRAND_TRANSITION} variant={1}>
                <TransitionBanner
                    forPreTransition
                    useCookieOnClose
                    locale={fullLocale}
                    siteName={siteName}
                    hostApp={HOSTAPP.TX}
                />
            </Experiment>
            <Experiment name={HA_GX_POST_BRAND_TRANSITION} variant={1}>
                <TransitionBanner
                    vgdc={vgdc}
                    forPreTransition={false}
                    useCookie={false}
                    locale={fullLocale}
                    siteName={siteName}
                    hostApp={HOSTAPP.TX}
                />
            </Experiment>
            {loginModalOpen && (
                <Experiment name={HA_IDA_V3_LOGIN_TEST}>
                    {({On, Off}) => (
                        <>
                            <On>
                                <LoginExperience
                                    useNewEndpoints
                                    {...loginProps}
                                />
                            </On>
                            <Off>
                                <LoginExperience {...loginProps} />
                            </Off>
                        </>
                    )}
                </Experiment>
            )}
            {!isUserLogged && (
                <Experiment name={HA_IDA_SERP_GOOGLEONETAP} variant={1}>
                    <GoogleOneTapLogin
                        displayIFrame={displayGoogleFrame}
                        onCredentialsAvailable={() =>
                            setDisplayNotificationJewel(() => true)
                        }
                        focus
                        closeOnFocusOut
                        loginSuccessCallback={handleLoginSuccess}
                    />
                </Experiment>
            )}
        </div>
    );
};

const mapStateToProps = ({
    header,
    searchParams: {params},
    system: {
        siteName,
        theme,
        isMobile,
        fullLocale,
        currency,
        brand,
        marketingOptInType
    },
    user,
    viewport: {breakpoint}
}: ReduxState): StateProps => ({
    siteName,
    theme,
    isMobile,
    isTouchDevice: isMobile || !media(breakpoint, {minWidth: 'LG_MIN'}),
    fullLocale,
    currency,
    brand,
    headerData: header,
    marketingOptInType: marketingOptInType.sign_up,
    preferencePageType: params.pinnedPropertyListing ? 'pinned_serp' : 'serp',
    isUserLogged:
        (user && user.isLoggedIn) ||
        header.hasOwnProperty('traveler') ||
        header.hasOwnProperty('owner')
});

const mapDispatchToProps = {
    setLoggedIn: userActions.setLoggedIn
};
export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withLingua({
        namespace: 'gx/globalization',
        category: 'rebrandingBrandTransition'
    })
)(HeaderBar);
